import { Auth } from '@aws-amplify/auth'
import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'

import { getDeviceAccessToken } from './device_auth_tokens'
import {
  type AuthRequestDiagnostics,
  startAuthDiagnostics,
  finishAuthDiagnostics,
  recordAuthForbidden,
} from './auth_diagnostics'

type AuthMode = 'user' | 'device' | 'public'

// Fired on `window` whenever a user-authenticated API request comes back with a
// 403. Apps subscribe to this to surface a "no access" message
// without the shared package needing to know about routing or notifications.
export const API_FORBIDDEN_EVENT = 'goose:api:forbidden'

// Fired on `window` when the interceptor blocks a request because the live
// Cognito session identity no longer matches the identity the app is displaying
// (stale `userState` in local storage — typically an incomplete logout/account
// switch on a shared device). Apps subscribe to this to force a real sign-out,
// which clears the stale local-storage session. Without this the blocked
// requests fail on every mount and the app can get stuck in a redirect loop
// until the user manually clears local storage.
export const SESSION_IDENTITY_MISMATCH_EVENT = 'goose:api:session-identity-mismatch'

// Fired on `window` when the Cognito session can no longer be renewed — either
// `Auth.currentSession()` rejects (the refresh token is expired/invalid so
// Amplify cannot mint a fresh access token) or API Gateway rejects a request
// with the `execute-api:Invoke` authorization error that an expired token
// produces. Apps subscribe to this to sign out and send the user to the
// sign-in page. This is kept separate from `API_FORBIDDEN_EVENT` (feature /
// policy access denials) so an expired token does not get mistaken for a
// "no access" denial and bounced between pages in an infinite redirect loop.
export const SESSION_REFRESH_FAILED_EVENT = 'goose:api:session-refresh-failed'

// API Gateway returns a 403 with this message when the attached JWT is missing,
// expired, or otherwise unauthorized at the IAM layer. This is NOT an
// application feature-access denial, so it must be treated as "re-authenticate"
// rather than "no access to this resource".
const EXECUTE_API_AUTH_ERROR = 'execute-api:Invoke'

/**
 * True when a 403 originates from API Gateway's IAM authorizer rejecting the
 * token (expired / missing / unauthorized) rather than the application denying
 * access to a feature. Detected via the distinctive `execute-api:Invoke`
 * message API Gateway returns in that case.
 */
export const isExpiredTokenForbidden = (error: unknown): boolean => {
  if (!axios.isAxiosError(error) || error.response?.status !== 403) return false
  const message: unknown = error.response?.data?.message ?? error.response?.data?.Message
  return typeof message === 'string' && message.includes(EXECUTE_API_AUTH_ERROR)
}

const dispatchWindowEvent = (name: string) => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(name))
  }
}

const apiV1 = (host: string) => `https://${host}/api/v1`
const apiRoot = (host: string) => `https://${host}`

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const env = (key: string): string => import.meta.env[key] ?? ''

const getClient = (url: string) => axios.create({ baseURL: url })

// Key under which the admin app persists its session (the identity shown in the
// UI). Kept in sync with `USER_STORE_KEY` in
// apps/admin/src/providers/session/helpers.ts. Read here (rather than imported)
// to avoid a shared-package -> app dependency.
const SESSION_STORE_KEY = 'userState'

/**
 * Returns the user id (Cognito `sub`) of the identity the app currently
 * *displays*, as persisted in local storage, or null if none / unreadable.
 */
const getDisplayedUserId = (): string | null => {
  try {
    const raw = localStorage.getItem(SESSION_STORE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { user?: { id?: string } } | null
    return parsed?.user?.id ?? null
  } catch {
    return null
  }
}

/**
 * Extracts the `sub` claim from a JWT without verifying the signature (the
 * token comes from Amplify's own trusted session; we only need to read the
 * subject to compare identities). Returns null if it can't be parsed.
 */
const getJwtSub = (jwt: string): string | null => {
  try {
    const payload = jwt.split('.')[1]
    if (!payload) return null
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { sub?: string }
    return json?.sub ?? null
  } catch {
    return null
  }
}

/**
 * Thrown when the live Cognito session identity does not match the identity the
 * app is displaying. This happens on shared devices when a previous user's
 * session lingers after an incomplete logout/account switch: the footer shows
 * user B while the live token still belongs to user A. Sending the request
 * anyway would attribute the action (e.g. `createdBy` on a booking) to the
 * wrong user, so we block it and force the app to re-authenticate.
 */
export class SessionIdentityMismatchError extends Error {
  constructor(displayedUserId: string, sessionUserId: string) {
    super(
      `Session identity mismatch: app is displaying user "${displayedUserId}" ` +
        `but the active session belongs to "${sessionUserId}". Request blocked to ` +
        `prevent acting as the wrong user. Please sign out and sign back in.`
    )
    this.name = 'SessionIdentityMismatchError'
  }
}

const setInterceptor = (client: AxiosInstance) => {
  // Each requester creates its own client; keep diagnostics local to that request.
  let diagnostic: AuthRequestDiagnostics | undefined
  client.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    diagnostic = startAuthDiagnostics(config)
    try {
      const authConfig = await Auth.currentSession()
      const token = authConfig.getAccessToken()
      const jwt = token.getJwtToken()

      // Guard against the live session and the displayed user having drifted
      // apart (shared-device / stale-session bug). Only enforce when the app
      // has a displayed user to compare against; unauthenticated/bootstrapping
      // states have no stored identity and fall through unchanged.
      const displayedUserId = getDisplayedUserId()
      const sessionUserId = getJwtSub(jwt)
      if (displayedUserId && sessionUserId && displayedUserId !== sessionUserId) {
        throw new SessionIdentityMismatchError(displayedUserId, sessionUserId)
      }

      ;(config.headers as AxiosHeaders).set('Authorization', `Bearer ${jwt}`)
      finishAuthDiagnostics(diagnostic, 'bearer')
    } catch (e) {
      finishAuthDiagnostics(diagnostic, e === 'No current user' ? 'optional_auth' : 'blocked', e)
      if (e instanceof SessionIdentityMismatchError) {
        // eslint-disable-next-line no-console
        console.error(e.message)
        // Signal the app to sign out and clear the stale local-storage session.
        // Otherwise the mismatch persists across remounts, blocking every
        // request and (combined with the 403 redirect handler) looping until
        // local storage is cleared by hand.
        dispatchWindowEvent(SESSION_IDENTITY_MISMATCH_EVENT)
        throw e
      }
      if (e === 'No current user') {
        ;(config.headers as AxiosHeaders).set('Authorization', 'OPTIONAL_AUTH')
      } else {
        // `Auth.currentSession()` auto-refreshes an expired access token using
        // the refresh token. Reaching here means that renewal failed — the
        // refresh token itself is expired/invalid — so there is no way to
        // recover the session silently. Signal the app to sign out and send the
        // user to the sign-in page instead of letting the request go out and
        // 403, which (via the forbidden handler) loops between pages.
        dispatchWindowEvent(SESSION_REFRESH_FAILED_EVENT)
        // A failed renewal must not turn an authenticated request into an
        // anonymous one. Let the caller handle the session error and retry.
        throw e
      }
    }
    return config
  })

  client.interceptors.response.use(undefined, (error: unknown) => {
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      // Record the forbidden outcome for diagnostics (breadcrumb + rate-limited
      // Sentry event), correlated with this request's session lookup. The
      // gateway request id lets us line a blocked request up with API Gateway
      // access logs.
      const headers = error.response.headers
      const gatewayRequestId =
        headers instanceof AxiosHeaders
          ? (headers.get('x-amzn-requestid') as string | undefined)
          : (headers as Record<string, string> | undefined)?.['x-amzn-requestid']
      recordAuthForbidden(diagnostic, gatewayRequestId)

      // An expired/invalid token produces an `execute-api:Invoke` 403 from API
      // Gateway. Treat it as "re-authenticate" (sign out -> sign-in) rather than
      // a feature-access denial, which would redirect to the dashboard and loop.
      if (isExpiredTokenForbidden(error)) {
        dispatchWindowEvent(SESSION_REFRESH_FAILED_EVENT)
      } else {
        dispatchWindowEvent(API_FORBIDDEN_EVENT)
      }
    }
    return Promise.reject(error)
  })
}

const setDeviceInterceptor = (client: AxiosInstance) => {
  client.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    try {
      const token = await getDeviceAccessToken()
      if (token) {
        ;(config.headers as AxiosHeaders).set('Authorization', `Bearer ${token}`)
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
    }
    return config
  })
}

const setPublicInterceptor = (client: AxiosInstance) => {
  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    ;(config.headers as AxiosHeaders).set('Authorization', 'NONE')
    return config
  })
}

const makeRequester = (urlFn: () => string, mode: AuthMode = 'user') => {
  return <T>(options: AxiosRequestConfig): Promise<T> => {
    // Capture caller stack synchronously, before going async.
    // Attached to any axios error as `cause` so Sentry shows app frames.
    const originError = new Error(`Request: ${(options.method ?? 'GET').toUpperCase()} ${options.url ?? ''}`)
    const client = getClient(urlFn())
    if (mode === 'user') setInterceptor(client)
    else if (mode === 'device') setDeviceInterceptor(client)
    else if (mode === 'public') setPublicInterceptor(client)

    return client(options)
      .then((response: AxiosResponse) => response.data as T)
      .catch((err: unknown) => {
        // Axios errors originate inside the lib's xhr adapter, so the stack has
        // no application frames. Attaching the caller-side Error as `cause`
        // lets Sentry's LinkedErrors integration surface where the call was
        // actually made from. Skipped for non-axios errors — their stack is
        // already useful and we don't want to overwrite it.
        if (axios.isAxiosError(err)) {
          ;(err as Error).cause = originError
        }
        return Promise.reject(err)
      })
  }
}

export const requestGooseAPI = makeRequester(() => apiV1(env('VITE_GOOSEAPI_BASE_URL')))
export const requestHatchAPI = makeRequester(() => apiV1(env('VITE_HATCH_BASE_URL')))

export const requestGooseAPIPublic = makeRequester(() => apiV1(env('VITE_GOOSEAPI_BASE_URL')), 'public')
export const requestDeviceGooseAPI = makeRequester(() => apiV1(env('VITE_GOOSEAPI_BASE_URL')), 'device')
export const requestAutomateExportGooseAPI = makeRequester(() => apiRoot(env('VITE_GOOSEAPI_AUTOMATED_EXPORT_URL')))
export const requestSearchGooseAPI = makeRequester(() => apiV1(env('VITE_GOOSEAPI_SEARCH_URL')))
export const requestCamerasGooseAPI = makeRequester(() => apiV1(env('VITE_GOOSEAPI_CAMERAS_URL')))
export const requestDataExportGooseAPI = makeRequester(() => apiV1(env('VITE_DATA_EXPORT_URL')))
export const requestReachGooseAPI = makeRequester(() => apiV1(env('VITE_GOOSEAPI_REACH_URL')))
export const requestPawGressReportGooseAPI = makeRequester(() => apiV1(env('VITE_GOOSEAPI_REPORT_PHOTOS_URL')))
export const requestGanderEmailAPI = makeRequester(() => apiRoot(env('VITE_GANDER_EMAIL_API_URL')))
export const requestGanderAPI = makeRequester(() => apiRoot(env('VITE_GANDER_API_URL')))

const VARIABLE_TEMPLATE = /\{\{(\w+)\}\}/gm

export const getAPIPath = (path?: string, options?: Record<string, string>) => {
  if (!path) {
    return ''
  }
  if (!options) {
    return path
  }

  const matches = path.matchAll(VARIABLE_TEMPLATE)
  for (const match of matches) {
    const [tpl, opt] = [...match]
    path = path.replace(tpl, options[opt])
  }
  return path
}

export const getAxiosErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const msg: string | null = error.response?.data?.message || error.response?.data?.error || null
    return msg ? msg.replaceAll('.', ' ') : msg
  }
  return null
}

export const getOpenSearchErrorMessage = (maybeAxiosError: unknown) => {
  let msg = 'Opensearch Error'
  if (axios.isAxiosError(maybeAxiosError)) {
    const error = maybeAxiosError as AxiosError
    msg += `\n${JSON.stringify(error.toJSON(), null, 2)}`
  } else if (maybeAxiosError instanceof Error) {
    msg += `\n${maybeAxiosError.stack}`
  }
  return msg
}

export const isHttpBadRequest = (error: unknown): boolean => {
  return axios.isAxiosError(error) && error.response?.status === 400
}

export const isHttpGone = (error: unknown): boolean => {
  return axios.isAxiosError(error) && error.response?.status === 410
}

export const isNotFound = (error: unknown): boolean => {
  return axios.isAxiosError(error) && error.response?.status === 404
}

export const isHttpUnauthorized = (error: unknown): boolean => {
  return axios.isAxiosError(error) && error.response?.status === 401
}

export const isHttpForbidden = (error: unknown): boolean => {
  return axios.isAxiosError(error) && error.response?.status === 403
}

export const isHttpTimeout = (error: unknown): boolean => {
  return axios.isAxiosError(error) && error.code === AxiosError.ETIMEDOUT
}
